import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Homepage from './routes/Homepage';
import Entrer from './routes/Entrer';
import ComingSoon from './routes/ComingSoon';
import ChoixEnfant from './routes/ChoixEnfant';
import ChoixHeros from './routes/ChoixHeros';
import OnboardingParent from './routes/OnboardingParent';
import Aventure from './routes/enfant/Aventure';
import ChildHub from './routes/enfant/ChildHub';
import EspaceParent from './routes/enfant/EspaceParent';
import Diagnostic from './routes/Diagnostic';
import DiagnosticIntro from './routes/DiagnosticIntro';
import './routes/Diagnostic.visual-fix.css';
import './routes/Diagnostic.portal-intro.css';
import MissionRentree from './routes/MissionRentree';
import CarteRentree from './routes/CarteRentree';
import MissionEtape from './routes/MissionEtape';
import MesPouvoirs from './routes/MesPouvoirs';
import SignIn from './routes/auth/SignIn';
import ForgotPassword from './routes/auth/ForgotPassword';
import UpdatePassword from './routes/auth/UpdatePassword';
import DeviceAuthorize from './routes/auth/DeviceAuthorize';
import AuthCallback from './routes/auth/AuthCallback';

function RequireParentSession({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<'checking' | 'ok' | 'unauth'>('checking');

  useEffect(() => {
    let alive = true;
    fetch('/api/parent/me', { credentials: 'include' })
      .then((response) => {
        if (alive) setState(response.ok ? 'ok' : 'unauth');
      })
      .catch(() => {
        if (alive) setState('unauth');
      });
    return () => { alive = false; };
  }, []);

  if (state === 'checking') {
    return <main style={{ padding: '2rem', textAlign: 'center' }}>Vérification de la session…</main>;
  }
  if (state === 'unauth') return <Navigate to="/entree/" replace />;
  return <>{children}</>;
}

function protectedRoute(node: ReactNode): JSX.Element {
  return <RequireParentSession>{node}</RequireParentSession>;
}

function DiagnosticEntryRedirect(): JSX.Element {
  const location = useLocation();
  return <Navigate to={`/enfant/diagnostic/intro/${location.search}`} replace />;
}

function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Homepage />} />

        {/* Connexion existante : Famille + Enseignant. */}
        <Route path="/entree/" element={<Entrer />} />
        <Route path="/login/" element={<Navigate to="/entree/" replace />} />
        <Route path="/signin/" element={<SignIn />} />
        <Route path="/forgot-password/" element={<ForgotPassword />} />
        <Route path="/update-password/" element={<UpdatePassword />} />
        <Route path="/device-authorize/" element={<DeviceAuthorize />} />
        <Route path="/auth/callback/" element={<AuthCallback />} />

        {/* Nouvelles inscriptions fermées jusqu'au lancement. */}
        <Route path="/bientot/" element={<ComingSoon />} />
        <Route path="/signup/" element={<Navigate to="/bientot/" replace />} />
        <Route path="/entree/famille/*" element={<Navigate to="/bientot/" replace />} />

        {/* Produit : accessible uniquement avec une session parent valide. */}
        <Route path="/famille/accueil/" element={protectedRoute(<ChoixEnfant />)} />
        <Route path="/famille/espace-parent/" element={protectedRoute(<EspaceParent />)} />
        <Route path="/onboarding/choix-heros/" element={protectedRoute(<ChoixHeros />)} />
        <Route path="/onboarding-parent/" element={protectedRoute(<OnboardingParent />)} />
        <Route path="/enfant/aventure/" element={protectedRoute(<Aventure />)} />
        <Route path="/enfant/hub/" element={protectedRoute(<ChildHub />)} />
        <Route path="/enfant/monde/" element={<Navigate to="/enfant/aventure/" replace />} />
        <Route path="/enfant/diagnostic/intro/" element={protectedRoute(<DiagnosticIntro />)} />
        <Route path="/enfant/diagnostic/questions/" element={protectedRoute(<Diagnostic />)} />
        <Route path="/enfant/diagnostic/" element={protectedRoute(<DiagnosticEntryRedirect />)} />
        <Route path="/enfant/mission/rentree/" element={protectedRoute(<MissionRentree />)} />
        <Route path="/enfant/mission/rentree/etape/" element={protectedRoute(<MissionEtape />)} />
        <Route path="/enfant/mission/rentree/carte/" element={protectedRoute(<CarteRentree />)} />
        <Route path="/enfant/mes-pouvoirs/" element={protectedRoute(<MesPouvoirs />)} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
